import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2019 semantics GmbH
// Date        : $Date: 2026-03-13 15:47:48 +0100 (Fri, 13 Mar 2026) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 161511 $
// Purpose     : lightweight viewer via swiper non-react component
// =============================================================================
// ---Third-party imports
import React from 'react';
// ---Framework imports
import log from 'uix/logging';
import { get, dict, flatten } from 'ldash';
import { useDimensions, useSelector, useState, useInView, useEffect, useActions, useMemo } from 'uix/hooks';
import { Actions, Sel } from 'uix/registries';
import { hashObj } from 'uix/utils';
import { decodeAttribH5 } from 'uix/wiki/utils';
import { funcPart } from 'uix/parts';
// lazy loadable component
import { Swiper } from 'uix/chunks';
//~ import Swiper from 'react-id-swiper'
// ---Local imports
import { wcUrl, fitWebcacheImage } from './utils';
import S from './swiper.css';
// ---Globals
const ViewerSlide = ({ height, width, url, page })=>{
    let replaceQueryParams = useActions(Actions.replaceQueryParams);
    let [ref, onScreen] = useInView({
        triggerOnce: false
    });
    useEffect(()=>{
        if (onScreen) {
            replaceQueryParams({
                id: page.id
            });
        }
    }, [
        onScreen
    ]);
    let style = {
        backgroundImage: onScreen ? `url(${url})` : undefined,
        width,
        height
    };
    return /*#__PURE__*/ _jsxs("div", {
        ref: ref,
        id: S.slide,
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: S.title,
                children: page.pagination
            }),
            /*#__PURE__*/ _jsx("div", {
                className: S.page,
                style: style
            })
        ]
    });
};
const SwiperSimpleViewer = ({ pages, selIndex })=>{
    let apiurl = useSelector(Sel.apiurl);
    let [elementSize, ref] = useDimensions({
        delta: 50
    });
    let slidesPerView = 1;
    // https://react-id-swiper.ashernguyen.site/example/default
    let params = {
        initialSlide: selIndex > -1 ? selIndex : 0,
        slidesPerView: slidesPerView,
        //~ freeMode: true,
        mousewheel: true,
        pagination: {
            el: '.swiper-pagination',
            type: 'progressbar'
        }
    };
    let divs = pages.map((page, i)=>{
        let { width, height, wcWidth } = fitWebcacheImage({
            aspectRatio: page.size[0] / page.size[1],
            width: Math.floor(elementSize.width / slidesPerView),
            height: elementSize.height
        });
        let url = wcUrl(apiurl, wcWidth, page.id);
        return /*#__PURE__*/ _jsx("div", {
            children: /*#__PURE__*/ _jsx(ViewerSlide, {
                url: url,
                width: width,
                height: height,
                page: page
            })
        }, page.id);
    });
    return /*#__PURE__*/ _jsx("div", {
        ref: ref,
        style: {
            width: '100%',
            height: '100%'
        },
        children: /*#__PURE__*/ _jsx(Swiper, {
            ...params,
            children: divs
        }, hashObj([
            params,
            pages,
            elementSize
        ]))
    });
};
// read structuredImageLocs into flat list but preserve parent ids
const getPagesFlat = (parent, level = 0, path = [])=>flatten((parent.children || []).filter((e)=>!e.link).map((e)=>e.datatype == 3 ? {
            ...e,
            pid: parent.id,
            level,
            parent,
            path
        } : getPagesFlat(e, level + 1, [
            ...path,
            e.id
        ])));
export const SwiperViewer = funcPart({
    partStyle: {
        height: '100%',
        overflow: 'hidden'
    }
}, ({ imageLocs, request })=>{
    let pages = useMemo(()=>getPagesFlat(imageLocs), [
        imageLocs
    ]);
    let selIndex = pages.findIndex((p)=>p.id == request.id);
    return /*#__PURE__*/ _jsx(SwiperSimpleViewer, {
        pages: pages,
        selIndex: selIndex
    });
});
